import React from 'react'
import { Alert, Box, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, TextField, Typography, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material'
import { clinvar } from '../api/clinvar'

const labels = { fetching: 'Vorschau wird geladen', ready: 'Vorschau bereit', applying: 'Wird übernommen', complete: 'Import abgeschlossen', failed: 'Import unterbrochen' }
export default function ClinVarImport({ onImported }) {
    const [open, setOpen] = React.useState(false)
    const [labs, setLabs] = React.useState([]), [jobs, setJobs] = React.useState([])
    const [labId, setLabId] = React.useState(''), [jobId, setJobId] = React.useState('')
    const [result, setResult] = React.useState(null), [page, setPage] = React.useState(0)
    const [error, setError] = React.useState(''), [busy, setBusy] = React.useState(false)
    const notified = React.useRef('')
    React.useEffect(() => {
        if (!open) return
        let active = true
        clinvar('/labs').then(data => {
            if (!active) return
            setLabs(data.labs); setJobs(data.jobs)
            setLabId(value => value || data.labs[0]?._id || '')
        }).catch(e => active && setError(e.message))
        return () => { active = false }
    }, [open])
    React.useEffect(() => {
        if (!open || !jobId) return
        let active = true, timer
        async function refresh() {
            try {
                const data = await clinvar(`/imports/${jobId}?page=${page}`)
                if (!active) return
                setResult(data)
                if (['fetching', 'applying'].includes(data.job.state)) timer = setTimeout(refresh, 2000)
                if (data.job.state === 'complete' && notified.current !== jobId) {
                    notified.current = jobId; onImported?.()
                }
            } catch (e) { if (active) setError(e.message) }
        }
        refresh()
        return () => { active = false; clearTimeout(timer) }
    }, [open, jobId, page, result?.job?.state, onImported])
    async function start() {
        setBusy(true); setError('')
        try {
            const { id } = await clinvar('/imports', { labId })
            setResult(null); setPage(0); setJobId(id)
        } catch (e) { setError(e.message) } finally { setBusy(false) }
    }
    async function commit() {
        setBusy(true); setError('')
        try {
            await clinvar(`/imports/${jobId}/commit`, {})
            setResult(value => ({ ...value, job: { ...value.job, state: 'applying', processed: 0 } }))
        } catch (e) { setError(e.message) } finally { setBusy(false) }
    }
    const lab = labs.find(l => l._id === labId)
    const job = result?.job, summary = job?.summary
    const running = ['fetching', 'applying'].includes(job?.state)
    return <>
        <Button variant="outlined" onClick={() => setOpen(true)}>Bewertungen aus ClinVar importieren</Button>
        <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="lg">
            <DialogTitle>ClinVar-Import</DialogTitle>
            <DialogContent>
                <Typography paragraph>Importiert die veröffentlichten Keimbahnbewertungen Ihres Labors. Fehlt ein Fall mit derselben Variante, entsteht ein gekennzeichneter Platzhalter ohne erfundene Patientendaten.</Typography>
                {error && <Alert severity="error">{error}</Alert>}
                <Box sx={{ display: 'flex', gap: 2, my: 2, flexWrap: 'wrap' }}>
                    <TextField select label="Labor" value={labId} onChange={e => { setLabId(e.target.value); setJobId(''); setResult(null) }} sx={{ minWidth: 260 }} disabled={running}>
                        {labs.map(l => <MenuItem key={l._id} value={l._id}>{l.shortName || l.name}</MenuItem>)}
                    </TextField>
                    <Button disabled={busy || running || !lab?.clinvar?.organizationId} onClick={start}>Neue Vorschau laden</Button>
                </Box>
                {lab && <Typography paragraph>Organization ID: {lab.clinvar?.organizationId || 'Noch nicht hinterlegt – bitte Laborverwaltung kontaktieren.'} · Letzter Import: {lab.clinvar?.lastImportedAt ? new Date(lab.clinvar.lastImportedAt).toLocaleString() : '—'}</Typography>}
                {jobs.filter(j => j.labId === labId).length > 0 && <TextField select fullWidth label="Frühere Importe / laufenden Import öffnen" value={jobs.some(j => j._id === jobId) ? jobId : ''} onChange={e => { setResult(null); setPage(0); setJobId(e.target.value) }} sx={{ mb: 2 }}>
                    {jobs.filter(j => j.labId === labId).map(j => <MenuItem key={j._id} value={j._id}>{new Date(j.createdAt).toLocaleString()} – {labels[j.state]}</MenuItem>)}
                </TextField>}
                {job && <>
                    <Typography variant="h6">{labels[job.state]} {running && <CircularProgress size={18} />}</Typography>
                    <Typography>{job.processed || 0} / {job.total ?? '…'} Varianten verarbeitet</Typography>
                    {job.error && <Alert severity="error">{job.error}</Alert>}
                    {summary && <Alert severity="info" sx={{ my: 2 }}>{summary.importable} importierbare Varianten · {summary.new} neue, {summary.updated} aktualisierte, {summary.unchanged} unveränderte und {summary.older} ältere SCVs · {summary.placeholders} vorgesehene Platzhalter · {summary.skipped} ausgelassen</Alert>}
                    <Typography variant="body2">Die Vorschau bleibt für diesen Import gespeichert. Frühere SCV-Versionen und ergänzte Falldaten bleiben erhalten. Fehlende Submissions werden nicht automatisch gelöscht.</Typography>
                    <Table size="small"><TableHead><TableRow><TableCell>ClinVar</TableCell><TableCell>HGQN / bestehende Fälle</TableCell><TableCell>Übernahme</TableCell></TableRow></TableHead><TableBody>
                        {result.items.map(item => <TableRow key={item._id}>
                            <TableCell><a href={`https://www.ncbi.nlm.nih.gov/clinvar/variation/${item.variationId}/`} target="_blank" rel="noreferrer">{item.variationId}</a></TableCell>
                            <TableCell>{item.preview.variantId}<br />{item.preview.cases?.map(c => c.internalCaseId || c.clinvarImport?.technicalId || c._id).join(', ')}</TableCell>
                            <TableCell>{item.preview.skipped ? item.preview.reason : item.preview.placeholderNeeded ? 'Neuer Platzhalter' : 'Bewertungen ergänzen; keine automatische Patientenzuordnung'}</TableCell>
                        </TableRow>)}
                    </TableBody></Table>
                    <Button disabled={!page} onClick={() => setPage(page - 1)}>Zurück</Button>
                    <Button disabled={(page + 1) * 50 >= (job.processed || 0)} onClick={() => setPage(page + 1)}>Weiter</Button>
                </>}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpen(false)}>Schließen</Button>
                <Button variant="contained" disabled={busy || !job?.completeSnapshot || !['ready', 'failed'].includes(job.state) || !summary?.importable} onClick={commit}>Geprüfte Vorschau übernehmen</Button>
            </DialogActions>
        </Dialog>
    </>
}
